/**
 * LabelUtils - Shared Label Generation Utility
 * 
 * SOLID Principle: Single Responsibility Principle (SRP) & DRY (Don't Repeat Yourself)
 * Purpose: Consolidates duplicate field label generation logic that was scattered
 * across ChartConfigBuilder and TooltipFormatter. Provides single source of truth
 * for converting field keys to human-readable labels.
 * 
 * Responsibilities:
 * - Convert field keys to human-readable labels
 * - Handle sensor name mapping from constants
 * - Provide consistent labeling across all components
 * 
 * Context: Field keys come in format 'measurement:field' (e.g., 'procv_co2:pCO2')
 * and need to be converted to readable labels (e.g., 'ProCV CO2 Partial Pressure')
 * using the sensor mapping constants.
 */

import { SENSOR_NAME_MAP, SENSOR_FIELDS } from '../../../constants/sensors';

export class LabelUtils {
  /**
   * Extracts base field key from potentially buoy-prefixed field key
   * 
   * @param {string} fieldKey - Format: 'measurement:field' or 'buoyId_measurement:field'
   * @returns {string} Base field key without buoy prefix
   * 
   * @example
   * LabelUtils.getBaseFieldKey('CT1002_procv_co2:pCO2') 
   * // Returns: 'procv_co2:pCO2'
   */
  static getBaseFieldKey(fieldKey) {
    // Check if the field key has a buoy prefix (contains underscore before colon)
    const colonIndex = fieldKey.indexOf(':');
    if (colonIndex !== -1) {
      const beforeColon = fieldKey.substring(0, colonIndex);
      const afterColon = fieldKey.substring(colonIndex);
      
      // If there's an underscore in the part before the colon, check if it's a buoy prefix
      const underscoreIndex = beforeColon.indexOf('_');
      if (underscoreIndex !== -1) {
        const possibleBuoyId = beforeColon.substring(0, underscoreIndex);
        // Only strip if it looks like a buoy ID (CT1002, CT1003, etc) - not virtual_
        if (/^[A-Z]{1,3}\d{3,4}$/.test(possibleBuoyId)) {
          return beforeColon.substring(underscoreIndex + 1) + afterColon;
        }
      }
      
      return fieldKey;
    }
    
    // Handle flattened format (no colon) - remove buoy prefix if present
    const underscoreIndex = fieldKey.indexOf('_');
    if (underscoreIndex !== -1) {
      // Check if the part before the first underscore looks like a buoy ID (CT1002, etc)
      const possibleBuoyId = fieldKey.substring(0, underscoreIndex);
      if (/^[A-Z]{1,3}\d{3,4}$/.test(possibleBuoyId)) {
        return fieldKey.substring(underscoreIndex + 1);
      }
    }
    
    return fieldKey;
  }

  /**
   * Extracts buoy ID from prefixed field key
   * 
   * @param {string} fieldKey - Format: 'buoyId_measurement:field'
   * @returns {string|null} Buoy ID or null if not prefixed
   */
  static getBuoyIdFromFieldKey(fieldKey) {
    const colonIndex = fieldKey.indexOf(':');
    if (colonIndex === -1) return null;
    
    const beforeColon = fieldKey.substring(0, colonIndex);
    const underscoreIndex = beforeColon.indexOf('_');
    
    if (underscoreIndex !== -1) {
      return beforeColon.substring(0, underscoreIndex);
    }
    
    return null;
  }

  /**
   * Converts field key to human-readable label
   * 
   * This is the single implementation that replaces duplicate logic in:
   * - ChartConfigBuilder._getFieldLabel()
   * - TooltipFormatter._getFieldLabel()
   * 
   * @param {string} fieldKey - Format: 'measurement:field' or 'buoyId_measurement:field'
   * @returns {string} Human-readable label
   * 
   * @example
   * LabelUtils.getFieldLabel('procv_co2:pCO2') 
   * // Returns: 'ProCV CO2 Partial Pressure [μatm]'
   * LabelUtils.getFieldLabel('CT1002_procv_co2:pCO2')
   * // Returns: 'CT1002 ProCV CO2 Partial Pressure [μatm]'
   */
  static getFieldLabel(fieldKey) {
    const buoyId = this.getBuoyIdFromFieldKey(fieldKey);
    const baseFieldKey = this.getBaseFieldKey(fieldKey);
    
    let sensor, field;
    
    // Check if field key contains colon (standard format: measurement:field)
    if (baseFieldKey.includes(':')) {
      [sensor, field] = baseFieldKey.split(":");
    } else {
      // Handle flattened format like 'virtual_temperature_msl_pressure_hpa'
      // Try to match known sensor prefixes (longest first to avoid partial matches)
      const sensorPrefixes = Object.keys(SENSOR_NAME_MAP).sort((a, b) => b.length - a.length);
      let foundSensor = null;
      
      for (const prefix of sensorPrefixes) {
        if (baseFieldKey.startsWith(prefix + '_')) {
          foundSensor = prefix;
          field = baseFieldKey.substring(prefix.length + 1); // +1 for the underscore
          break;
        } else if (baseFieldKey === prefix) {
          // Exact match case
          foundSensor = prefix;
          field = prefix; // Use sensor name as field name
          break;
        }
      }
      
      if (foundSensor) {
        sensor = foundSensor;
      } else {
        // Fallback: use the entire key as the field name
        sensor = 'unknown';
        field = baseFieldKey;
      }
    }
    
    // Get pretty names from sensor configuration constants
    const prettySensor = SENSOR_NAME_MAP?.[sensor] || sensor;
    const prettyField = SENSOR_FIELDS?.[sensor]?.[field] || field;
    
    const baseLabel = `${prettySensor} ${prettyField}`;
    
    // If we have a buoy ID prefix, add it to the label
    return buoyId ? `${buoyId} ${baseLabel}` : baseLabel;
  }

  /**
   * Extracts sensor name from field key
   * 
   * @param {string} fieldKey - Format: 'measurement:field'
   * @returns {string} Sensor measurement name
   */
  static getSensorName(fieldKey) {
    const [sensor] = fieldKey.split(":");
    return SENSOR_NAME_MAP?.[sensor] || sensor;
  }

  /**
   * Extracts field name from field key
   * 
   * @param {string} fieldKey - Format: 'measurement:field'
   * @returns {string} Field name
   */
  static getFieldName(fieldKey) {
    const [sensor, field] = fieldKey.split(":");
    return SENSOR_FIELDS?.[sensor]?.[field] || field;
  }

  /**
   * Creates short label for compact displays (e.g., arrow labels)
   * 
   * @param {string} fieldKey - Format: 'measurement:field'
   * @returns {string} Short label (usually just the field name)
   */
  static getShortLabel(fieldKey) {
    const fullLabel = this.getFieldLabel(fieldKey);
    // Return last word for compact display (e.g., "Direction" from "Wave Direction")
    return fullLabel.split(' ').pop();
  }

  /**
   * Gets axis label for Y-axis display (field name only, no sensor prefix)
   * 
   * @param {string} fieldKey - Format: 'measurement:field' or flattened format
   * @returns {string} Field name from sensors.js constants
   * 
   * @example
   * LabelUtils.getAxisLabel('virtual_waves:hs_m')
   * // Returns: 'Sig Wave Height [m]'
   * LabelUtils.getAxisLabel('virtual_waves_hs_m')
   * // Returns: 'Sig Wave Height [m]'
   */
  static getAxisLabel(fieldKey) {
    const baseFieldKey = this.getBaseFieldKey(fieldKey);
    
    let sensor, field;
    
    // Check if field key contains colon (standard format: measurement:field)
    if (baseFieldKey.includes(':')) {
      [sensor, field] = baseFieldKey.split(":");
    } else {
      // Handle flattened format like 'virtual_waves_hs_m'
      const sensorPrefixes = Object.keys(SENSOR_NAME_MAP).sort((a, b) => b.length - a.length);
      let foundSensor = null;
      
      for (const prefix of sensorPrefixes) {
        if (baseFieldKey.startsWith(prefix + '_')) {
          foundSensor = prefix;
          field = baseFieldKey.substring(prefix.length + 1);
          break;
        } else if (baseFieldKey === prefix) {
          foundSensor = prefix;
          field = prefix;
          break;
        }
      }
      
      if (foundSensor) {
        sensor = foundSensor;
      } else {
        sensor = 'unknown';
        field = baseFieldKey;
      }
    }
    
    // Get just the field name from sensor configuration constants
    const prettyField = SENSOR_FIELDS?.[sensor]?.[field] || field;
    
    return prettyField;
  }
}

export default LabelUtils;